import React from 'react';

// SVG waveform for an audio post. Width is proportional to duration (capped),
// height fixed. Rendered with neon rainbow gradient stroke.
export const Waveform = ({ duration = 0, maxWidth = 240, height = 22, className = '', dataTestId }) => {
  // Determine bars based on duration; clamp.
  const bars = Math.max(18, Math.min(72, Math.round(duration * 3)));
  const widthPx = Math.max(60, Math.min(maxWidth, 28 + duration * 18));

  // Deterministic pseudo-random heights based on bar index (so the same audio
  // doesn't shimmer between renders).
  const heights = React.useMemo(() => {
    const out = [];
    for (let i = 0; i < bars; i += 1) {
      // mix of sin curves for a pleasant shape
      const a = Math.abs(Math.sin(i * 0.35 + 1.1));
      const b = Math.abs(Math.sin(i * 0.92 + 0.3)) * 0.6;
      const c = Math.abs(Math.sin(i * 0.18 + 2.2)) * 0.3;
      out.push(0.3 + a * 0.55 + b * 0.2 + c * 0.15);
    }
    return out;
  }, [bars]);

  const gradId = React.useMemo(() => `wave-grad-${Math.random().toString(36).slice(2, 8)}`, []);
  const barW = widthPx / bars - 1;

  return (
    <svg
      width={widthPx}
      height={height}
      viewBox={`0 0 ${widthPx} ${height}`}
      className={`audio-post__wave ${className}`}
      data-testid={dataTestId}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={gradId} gradientTransform="rotate(0)">
          {/* Stops read from CSS variables published per-theme on
              <html>. See `--wave-stop-1..4` in index.css.
                DAY:   pink → peach → yellow → mint
                NIGHT: pink → pink-purple → light-purple → light-blue
              This is what makes the wave automatically match the
              day/night palette. Previously these were hardcoded to the
              day stops, which rendered as pink/peach/yellow/mint at
              night too — the bug user reported. */}
          <stop offset="0%" stopColor="var(--wave-stop-1)" />
          <stop offset="35%" stopColor="var(--wave-stop-2)" />
          <stop offset="70%" stopColor="var(--wave-stop-3)" />
          <stop offset="100%" stopColor="var(--wave-stop-4)" />
        </linearGradient>
      </defs>
      {heights.map((h, i) => {
        const x = i * (barW + 1) + 0.5;
        const barH = Math.max(2, h * height);
        const y = (height - barH) / 2;
        return (
          <rect
            key={i}
            x={x}
            y={y}
            width={Math.max(1.2, barW)}
            height={barH}
            rx={Math.min(1.5, barW / 2)}
            fill={`url(#${gradId})`}
            opacity={0.95}
          />
        );
      })}
    </svg>
  );
};
